import { forwardRef } from 'react';
import { Eye, EyeClosed } from 'lucide-react';
import { useMeasure } from '@/hooks/use-measure';
import { Button, ButtonProps } from '@/components/button';
import { Card as BaseCard, CardContent, CardProps } from '@/components/card';
import { cn, memoClassNames } from '@/utils';

export type FlipcardProps = React.ComponentProps<'div'> & {
  front?: React.ReactNode;
  back?: React.ReactNode;
  flip?: boolean;
  height?: string | number;
  slots?: {
    Card?: React.ElementType;
  };
  slotProps?: {
    card?: CardProps;
  };
};

const classes = memoClassNames('Flipcard', [
  'root',
  'card',
  'content',
  'button',
]);

export const Flipcard = forwardRef<HTMLDivElement, FlipcardProps>(
  (props, ref) => {
    const {
      front,
      back,
      flip,
      children,
      className,
      height,
      slots = {},
      slotProps,
      ...others
    } = props;

    const { Card = BaseCard } = slots;

    const [frontRef, frontDimensions] = useMeasure();
    const [backRef, backDimension] = useMeasure();

    const sideSize = {
      front: frontDimensions.height || 0,
      back: backDimension.height || 0,
    };

    console.log(sideSize);
    const cardHeight = Math.max(sideSize.front, sideSize.back) || 'auto';

    return (
      <div
        className={cn(classes.root, 'mm:w-full', className)}
        data-flipped={flip ? true : undefined}
        ref={ref}
        {...others}
      >
        <div
          className="mm:relative mm:transition-transform mm:duration-600 mm:transform-3d mm:in-data-flipped:rotate-y-180"
          style={{ height: cardHeight }}
        >
          {['front', 'back'].map((side) => {
            const ref = { front: frontRef, back: backRef }[side];
            const content = { front, back }[side];

            return (
              <div
                key={side}
                className={cn(
                  'mm:absolute mm:inset-x-0 mm:top-0 mm:min-h-full mm:backface-hidden',
                  { 'mm:rotate-y-180': side === 'back' }
                )}
                data-side={side}
              >
                <Card
                  {...slotProps?.card}
                  ref={ref}
                  className={cn(
                    classes.card,
                    'mm:flex-col mm:justify-center mm:border-2 mm:border-card-foreground mm:shadow-none',
                    slotProps?.card?.className
                  )}
                >
                  {typeof content === 'string' ? (
                    <FlipcardContent>{content}</FlipcardContent>
                  ) : (
                    content
                  )}
                </Card>
              </div>
            );
          })}
        </div>
        {children}
      </div>
    );
  }
);
Flipcard.displayName = 'Flashcard';

export const FlipcardContent = ({
  className,
  ...others
}: React.ComponentProps<'div'>) => (
  <CardContent
    {...others}
    className={cn(classes.content, 'mm:text-center mm:p-8', className)}
  />
);
FlipcardContent.displayName = 'FlipcardContent';

export type FlipcardButtonProps = ButtonProps & {
  flipped?: boolean;
  label?: string;
};

export const FlipcardButton = ({
  flipped,
  className,
  label,
  children,
  ...others
}: FlipcardButtonProps) => {
  const rootClassName = cn(
    classes.button,
    // { 'mm:flex-row-reverse': flipped },
    className
  );
  return (
    <Button
      className={rootClassName}
      startIcon={!flipped ? <Eye /> : <EyeClosed />}
      {...others}
    >
      {children || label}
    </Button>
  );
};

FlipcardButton.displayName = 'FlipcardButton';
